<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import { QueueRelationModel } from '@/components/task-forms/models';
import { ListBulletIcon, TrashIcon } from 'tir-style-system/icons/outline';
import { TirPmSvg, TirPmButton, TirPmButtonVariantEnum, TirPmButtonSizeEnum } from 'tir-components';
import { TirPmPopover } from 'tir-components';
import { onMounted } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

/**Параметры */
const props = defineProps({
  /**id проекта */
  projectId: { type: Number, required: true },
  /**Очередь задачи */
  queue: { type: Object as PropType<QueueRelationModel> },
  editing: { type: Boolean, default: true },
});
/**События */
const emit = defineEmits<{
  /**Удаление задачи из очереди */
  remove: [queueId: string];
}>();

/**Ссылка на компонент поповера */
const $confirmPopover = ref<InstanceType<typeof TirPmPopover>>();

const router = useRouter();

/**Ссылка на DOM */
const $queue = ref<HTMLElement>();

/**Открепить очередь */
const removeQueue = () => {
  emit('remove', props.queue.Id);
  $confirmPopover.value?.close();
};

/**Перейти в очередь */
const computedRoute = computed(() => {
  return router.resolve({
    name: 'queue',
    params: {
      projectId: props.projectId,
      namespaceId: props.queue.Namespace.Id,
      queueId: props.queue.Id,
    },
  });
});

defineExpose({
  queueItem: $queue,
});
</script>
<template>
  <RouterLink ref="$queue" class="queues-list-item" :to="computedRoute" @mousedown.middle.prevent>
    <div class="queues-list-item_queue">
      <ListBulletIcon />
      {{ queue.Name }}
    </div>
    <div class="queues-list-item_namespace">
      <TirPmSvg
        :url="queue.Namespace.Icon"
        class="queues-list-item_namespace-icon"
        :color="queue.Namespace.Color"
      />
      {{ queue.Namespace.Name }}
    </div>
    <div class="queues-list-item_remove" v-if="editing">
      <TirPmPopover
        ref="$confirmPopover"
        :custom-position="{
          right: '0rem',
          bottom: '0.25rem',
        }"
        @click.stop.prevent
      >
        <TirPmButton
          v-if="editing"
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
        >
          <template #leftSlot>
            <TrashIcon />
          </template>
        </TirPmButton>
        <template #content>
          <div class="queues-list-item__confirm-popover">
            <span class="queues-list-item__confirm-popover-message"> Открепить очередь? </span>
            <div class="queues-list-item__confirm-popover-buttons">
              <TirPmButton
                :variant="TirPmButtonVariantEnum.PrimaryState"
                :size="TirPmButtonSizeEnum.Small"
                @click.stop="removeQueue"
              >
                Открепить
              </TirPmButton>
              <TirPmButton
                :variant="TirPmButtonVariantEnum.SecondaryState"
                :size="TirPmButtonSizeEnum.Small"
                @click.stop="$confirmPopover?.close()"
              >
                Отмена
              </TirPmButton>
            </div>
          </div>
        </template>
      </TirPmPopover>
    </div>
  </RouterLink>
</template>
<style lang="scss" scoped>
.queues-list-item {
  display: flex;
  align-items: center;
  width: 100%;
  height: 3rem;
  padding: 0 0.625rem;
  transition: 0.2s linear;
  border: 0.0625rem solid var(--neutral-translucent-10, rgba(39, 45, 55, 0.1));
  gap: 1.5rem;
  cursor: pointer;
  &__confirm-popover {
    border-radius: 0.5rem;
    background: var(--bg-base, #fff);
    padding: var(--12, 0.75rem);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  &__confirm-popover-message {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5rem;
  }
  &__confirm-popover-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  & > div {
    display: flex;
    gap: 0.25rem;
    max-width: 50%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
    font-weight: 500;
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
  &_queue {
    color: var(--text-primary, #272d37);
  }
  &_namespace {
    display: flex;
    align-items: center;
    margin-left: auto;
    color: var(--text-secondary, #686c73);
  }
  &_namespace-icon {
    font-size: 1.5rem;
  }
  &_remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    transition: 0.2s linear;
    border-radius: 0.375rem;
    cursor: pointer;
    &:hover {
      background: var(--neutral-translucent-5);
    }
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
}
</style>
